import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { ChartTooltip } from 'pages/HrAnalytics/components/ChartTooltip'
import { getAnalyticsTrends } from 'features/operations/opsAnalyticsSlice'
import { AppDispatch, RootState } from 'store'
import { Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { ITrendDualPoint } from 'types/opsTrendsPage.interface'
import { TrendsGrid, TrendCard } from './TrendsPage.styled'

const AXIS_TICK = { fontSize: 11, fill: tokens.text.muted }

const mapTrendPoints = (points: { date: string; value: number }[] | undefined) =>
  (points ?? []).map((p) => ({
    label: p.date,
    value: p.value,
  }))

export const TrendsPage = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { trendsByMetric, loaded, isLoading } = useSelector((s: RootState) => s.opsAnalytics)

  useEffect(() => {
    dispatch(getAnalyticsTrends({ metric: 'production' }))
    dispatch(getAnalyticsTrends({ metric: 'availability' }))
    dispatch(getAnalyticsTrends({ metric: 'utilization' }))
  }, [dispatch])

  const productionPoints = useMemo(
    () => mapTrendPoints(trendsByMetric.production?.points),
    [trendsByMetric.production],
  )

  const dualPoints: ITrendDualPoint[] = useMemo(() => {
    const availability = trendsByMetric.availability?.points ?? []
    const utilization = trendsByMetric.utilization?.points ?? []
    return availability.map((point, i) => ({
      label: point.date,
      availability: point.value,
      utilization: utilization[i]?.value ?? 0,
    }))
  }, [trendsByMetric.availability, trendsByMetric.utilization])

  const hasData = productionPoints.length > 0 || dualPoints.length > 0

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex column gap='0.25rem'>
          <H1WithBack title='Trends' arrow />
          <BodyText grey>Shift aggregates — patterns emerge after the first finalized shifts.</BodyText>
        </Flex>
        {loaded && !isLoading && !hasData ? (
          <NoData text='Trends unlock once shift aggregates exist.' />
        ) : (
          <TrendsGrid>
            <TrendCard>
              <ContainerHeader>
                <TitleText weight='bold'>Production</TitleText>
              </ContainerHeader>
              <ContainerContent>
                {productionPoints.length ? (
                  <ResponsiveContainer width='100%' height={240}>
                    <AreaChart data={productionPoints}>
                      <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
                      <XAxis dataKey='label' tick={AXIS_TICK} tickLine={false} />
                      <YAxis tick={AXIS_TICK} tickLine={false} />
                      <Tooltip content={<ChartTooltip formatter={(value: number) => `${value.toLocaleString()} t`} />} />
                      <Area
                        type='monotone'
                        dataKey='value'
                        name='Production'
                        stroke={tokens.accent.base}
                        strokeWidth={2}
                        fill={`${tokens.accent.base}33`}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <NoData text='No production trend data yet.' />
                )}
              </ContainerContent>
            </TrendCard>
            <TrendCard>
              <ContainerHeader>
                <TitleText weight='bold'>Availability vs utilization</TitleText>
              </ContainerHeader>
              <ContainerContent>
                {dualPoints.length ? (
                  <ResponsiveContainer width='100%' height={240}>
                    <LineChart data={dualPoints}>
                      <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
                      <XAxis dataKey='label' tick={AXIS_TICK} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={AXIS_TICK} tickLine={false} />
                      <Tooltip content={<ChartTooltip formatter={(value: number) => `${value}%`} />} />
                      <Line type='monotone' dataKey='availability' name='Availability' stroke={tokens.status.info.fg} strokeWidth={2} dot={false} />
                      <Line type='monotone' dataKey='utilization' name='Utilization' stroke={tokens.accent.base} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <NoData text='No availability or utilization trend data yet.' />
                )}
              </ContainerContent>
            </TrendCard>
          </TrendsGrid>
        )}
        <OtherText grey style={{ marginTop: '1rem' }}>
          Additional trend series (losses, coordination, safety) appear as more analytics endpoints return data.
        </OtherText>
      </PageWrapper>
    </>
  )
}
